// src/components/BaseDropdown.tsx

import React, { useState, useRef, useEffect, ReactNode } from 'react';
import { ChevronDown, ChevronUp } from 'react-feather';
import { BaseDropdownProps } from '../types/Dropdown';
import './BaseDropdown.scss';

interface Props extends BaseDropdownProps {
    toggleContent: ReactNode;
    menuContent: ReactNode;
}

export const BaseDropdown: React.FC<Props> = ({
    toggleContent,
    menuContent,
    disabled = false,
    className = '',
    forceOpen,
    onOpenChange,
}) => {
    const [isOpenInternal, setIsOpenInternal] = useState(false);
    const [dropUpwards, setDropUpwards] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    const isOpen = forceOpen !== undefined ? forceOpen : isOpenInternal;

    const setIsOpen = (value: boolean) => {
        setIsOpenInternal(value);
        onOpenChange?.(value);
    };
    const calculateDropDirection = () => {
        if (dropdownRef.current) {
            const buttonRect = dropdownRef.current.getBoundingClientRect();
            const viewportHeight = window.innerHeight;
            const spaceBelow = viewportHeight - buttonRect.bottom;
            const spaceAbove = buttonRect.top;

            const estimatedMenuHeight = 200; // Max height
            const MINIMUM_SPACE = 20;

            if (spaceBelow < estimatedMenuHeight + MINIMUM_SPACE &&
                spaceAbove > estimatedMenuHeight + MINIMUM_SPACE) {
                setDropUpwards(true);
            } else {
                setDropUpwards(false);
            }
        }
    };

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => {
        if (isOpen) {
            calculateDropDirection();
        }
    }, [isOpen]);

    return (
        <div className={`base-dropdown ${className}`} ref={dropdownRef}>
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="dropdown-toggle"
                disabled={disabled}
            >
                {toggleContent}
                {isOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            </button>
            {isOpen && (
                <div className={`dropdown-menu ${dropUpwards ? 'upwards' : ''}`}>
                    {menuContent}
                </div>
            )}
        </div>
    );
};